/**
 * The tags a description may **carry**.
 *
 * This mirrors `server/app/core/rich_text.py`'s allowlist deliberately, and the
 * two are pinned to each other by a drift test. The server sanitises on the way
 * *out*, so this is not the security boundary — it is the shape a stored
 * description is allowed to have, migrated rows included.
 *
 * What a *paste* may introduce is the narrower `RICH_TEXT_PASTE_TAGS` below,
 * and the two lists answer different questions: this one has to admit
 * everything #292 ratified for storage, while a paste has to reproduce what
 * survives draft-js's importer in modsy. The scrub is held to the narrower one;
 * keeping a tag on paste that this list would strip is what leaves the editor
 * showing something the saved value does not.
 */
export const RICH_TEXT_TAGS: readonly string[] = [
  "A",
  "B",
  "BR",
  "DEL",
  "DIV",
  "EM",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "I",
  "LI",
  "OL",
  "P",
  "S",
  "SPAN",
  "STRIKE",
  "STRONG",
  "SUB",
  "SUP",
  "TABLE",
  "TBODY",
  "TD",
  "TFOOT",
  "TH",
  "THEAD",
  "TR",
  "U",
  "UL",
];

/**
 * The attributes each tag keeps — and the anchor is the only tag that keeps any.
 *
 * `href` and `target` are exactly what the donor's exporter writes on a link
 * (`draftJsUtils.tsx:36-48`), so together they are what a modsy description
 * *is*. Pinned tag-by-tag to the Python allowlist by the drift test beside the
 * tag list, where Python spells the same pair across two options because `nh3`
 * restricts `target` to the literal `_blank`.
 *
 * `title` was on this list until #410. #292 ratified "attributes only
 * `href`/`title` on `a`"; the owner amended that on #410 to drop it, because no
 * path that writes a description can produce one — the exporter above emits
 * `{href, target}` and drops the rest, and 0 of 5,949 migrated rows carry one.
 * An allowlist entry nothing can reach is surface without a purpose.
 *
 * The **value** is not constrained here, because the editor constrains it at a
 * later seam instead: every emit normalises `target` across the whole field
 * (`rich-text-field.tsx`, `normaliseAnchors`), which is the donor's export
 * rule. Python enforces `_blank` again on the way out.
 */
export const RICH_TEXT_KEPT_ATTRIBUTES: Readonly<Record<string, readonly string[]>> = {
  A: ["href", "target"],
};

/**
 * The narrower list a **paste** is held to: the tags draft-js's HTML importer
 * gives a meaning to, and nothing else.
 *
 * `RICH_TEXT_TAGS` above says what a description may *contain* — it mirrors
 * Python's sanitiser, and a stored description is measured against it. This says
 * what a paste may *introduce*, which is a different question with a different
 * answer, because in modsy a paste goes through draft-js's model on the way in
 * and only survives as far as that model can describe it:
 *
 *   - **Blocks**: `getBlockTypeFromTagName` answers `li`, `blockquote`, `h1`-`h6`,
 *     `pre` and `figure`, and everything else with `unstyled`
 *     (`draft-js-import-element/lib/stateFromElement.js:235-291`). Of those,
 *     `blockquote`, `pre` and `figure` are not in `RICH_TEXT_TAGS`, so they
 *     cannot be kept here either — a tag kept on paste and stripped on save is
 *     the one failure this module's header calls out.
 *   - **Inline styles**: `addStyleFromTagName` answers `b`/`strong`, `i`/`em`,
 *     `u`/`ins`, `code`, and `s`/`del` — and nothing else (`:593-632`). So
 *     `strike`, `sub` and `sup` keep their characters and lose their meaning.
 *   - **Everything else** is unwrapped. A `span` carries nothing modelled, and a
 *     table's structure is not modelled at all.
 *
 * Both spellings of each style stay, rather than being folded onto the one
 * `stateToHTML` exports (`b` → `strong`, `i` → `em`, `s` → `del`): the pair
 * renders identically, `RICH_TEXT_TAGS` stores either, and rewriting them would
 * be churn with nothing observable on the other side of it.
 *
 * Owner ruling D3 on #410. The paste path alone changed, exactly as scoped —
 * a description already stored with a table still renders its table.
 */
export const RICH_TEXT_PASTE_TAGS: readonly string[] = [
  "A",
  "B",
  "BR",
  "DEL",
  "DIV",
  "EM",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "I",
  "LI",
  "OL",
  "P",
  "S",
  "STRONG",
  "U",
  "UL",
];

/**
 * Tags a paste turns into something else rather than unwrapping.
 *
 * A `td` is the one place unwrapping would lose something draft-js keeps.
 * `table`, `tbody`, `thead`, `tfoot` and `tr` are all in draft-js's
 * `SPECIAL_ELEMENTS` — they cannot hold text directly, so they render no block
 * of their own (`stateFromElement.js:332`) — but a `td` is neither inline nor
 * special, which makes it an ordinary block element and so an `unstyled` block,
 * which `stateToHTML` writes as a `<p>`. Unwrapping the cells instead would run
 * every cell's text together into one paragraph; modsy gives you one paragraph
 * per cell, in order.
 */
const PASTE_REWRITES: Readonly<Record<string, string>> = { TD: "p", TH: "p" };

/** The schemes a description legitimately links to. */
const KEPT_SCHEMES = ["http:", "https:", "mailto:", "tel:"];

const allowed = new Set(RICH_TEXT_PASTE_TAGS);

function keepsHref(value: string): boolean {
  try {
    return KEPT_SCHEMES.includes(new URL(value, window.location.origin).protocol);
  } catch {
    return false;
  }
}

/**
 * Pasted markup, reduced to the allowlist.
 *
 * Unknown tags are **unwrapped**, not dropped — their text survives — which is
 * what the server does too, so a paste looks the same before and after a save.
 */
export function scrubPastedHtml(html: string): string {
  const host = document.createElement("div");
  host.innerHTML = html;

  const walk = (node: Element) => {
    for (const child of [...node.children]) walk(child);

    if (!allowed.has(node.tagName)) {
      const rewrite = PASTE_REWRITES[node.tagName];
      if (rewrite) {
        const block = document.createElement(rewrite);
        block.append(...node.childNodes);
        node.replaceWith(block);
        return;
      }
      node.replaceWith(...node.childNodes);
      return;
    }
    for (const attribute of [...node.attributes]) {
      const kept = RICH_TEXT_KEPT_ATTRIBUTES[node.tagName] ?? [];
      if (!kept.includes(attribute.name)) node.removeAttribute(attribute.name);
    }
    const href = node.getAttribute("href");
    if (href !== null && !keepsHref(href)) node.removeAttribute("href");
  };

  for (const child of [...host.children]) walk(child);
  return host.innerHTML;
}
